/**
 * THE ROOT. One question first, then everything else follows from the answer.
 *
 * GET /api/me is the first call this application makes, before a single screen renders. It comes
 * back with a role, and the role decides which of two products a person is looking at: the office
 * view, which is the day's schedule, the client book, the money, the promises and the crew, or a
 * cleaner's own visits and their job sheets and nothing else.
 *
 * TWO ROUTES ARE ANSWERED BEFORE THAT QUESTION IS ASKED. `/r/:token` is the page a client opens from
 * a link to her invoice or her visit report, and `PAYMENT_RETURN_PATH` beside it is the page Stripe
 * sends her back to, which holds no token and reads nothing. The person on either is not signed in
 * and never will be (CLAUDE.md binding rule 5: no client login), so the `/r/` prefix is checked
 * before the boot effect runs and /api/me is never called for it. Both render with no shell and no
 * navigation.
 *
 * THE SERVER ENFORCES THE ROLE TOO, AND THE SERVER IS THE ONE THAT COUNTS. `officeOnly()` sits on
 * /api/schedule, /api/clients, /api/invoices, /api/breaches and the rest of the office surface,
 * `ownersOnly()` sits on the compensation endpoints, and the row-level predicates in
 * `docs/identity.md` live in middleware. So a cleaner who edits her own JavaScript and renders the
 * client book gets a 403 and an empty screen. CLAUDE.md binding rule 6: hiding something in the
 * interface is UX, never the control. What the branch below decides is what is worth drawing,
 * never what is permitted, and those are different questions.
 *
 * The bell in the header counts unread notifications. It is fetched once when the person is known
 * and again on every route change, and never on a timer: the phone is in a car on mobile data. The
 * number beside Enquiries in the rail rides the same schedule, from an endpoint that answers with
 * two counts and no rows, so a digit in the navigation costs one cheap request and never a poll.
 *
 * AND THE DAY RIDES THAT SAME SCHEDULE, for the same reason and after a worse defect. `me.today`
 * is the business day as the server counts it, and every screen that says "Today", offers
 * yesterday and tomorrow, or refuses a day in the past reads that one string. It was answered once
 * and never asked for again, so a tab left open overnight kept yesterday's word for today: the
 * schedule drew the wrong day under the right heading and the date box would not take the real
 * date. Nobody reloads a tab at midnight. So the day is asked for again on every route change and
 * once more when the tab comes back to the front, both of them things a person did, and neither of
 * them a poll. `withToday` in `session.ts` hands back the same object when the day has not moved,
 * which is what keeps that out of a loop.
 *
 * A refusal from /api/me is not an error state, it is a screen: `SignedOut` explains which of the
 * three refusals happened, in the words the server used.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation as usePlace, useNavigate } from 'react-router-dom';
import { CALL_WALL_KEY, PAYMENT_RETURN_PATH } from '@fk/shared';
import {
  ApiError,
  countEnquiries,
  countTasks,
  getMe,
  listNotifications,
  onSessionEnded,
  type Me,
} from './api.ts';
import { AppShell, type ExternalLink, type NavItem } from './components/AppShell.tsx';
import { Busy } from './components/Notice.tsx';
import { RingingNow } from './components/RingingNow.tsx';
import { AgreementDetail } from './screens/AgreementDetail.tsx';
import { Calls } from './screens/Calls.tsx';
import { CallDetail } from './screens/CallDetail.tsx';
import { Campaigns } from './screens/Campaigns.tsx';
import { ClientDetail } from './screens/ClientDetail.tsx';
import { ClientList } from './screens/ClientList.tsx';
import { ClosedDays } from './screens/ClosedDays.tsx';
import { Crew } from './screens/Crew.tsx';
import { Enquiries } from './screens/Enquiries.tsx';
import { Find } from './screens/Find.tsx';
import { HomeDetail } from './screens/HomeDetail.tsx';
import { InvoiceDetail } from './screens/InvoiceDetail.tsx';
import { Issues } from './screens/Issues.tsx';
import { JobSheet } from './screens/JobSheet.tsx';
import { Messages } from './screens/Messages.tsx';
import { Money } from './screens/Money.tsx';
import { MyWork } from './screens/MyWork.tsx';
import { NewClient } from './screens/NewClient.tsx';
import { Notifications } from './screens/Notifications.tsx';
import { PaymentReturn } from './screens/PaymentReturn.tsx';
import { Promises } from './screens/Promises.tsx';
import { PublicPage } from './screens/PublicPage.tsx';
import { Settings } from './screens/Settings.tsx';
import { SignedOut } from './screens/SignedOut.tsx';
import { Sources } from './screens/Sources.tsx';
import { Spend } from './screens/Spend.tsx';
import { Tasks } from './screens/Tasks.tsx';
import { Today } from './screens/Today.tsx';
import { VisitDetail } from './screens/VisitDetail.tsx';
import { withToday } from './session.ts';

/**
 * FIVE WORDS ON THE PHONE, TEN IN THE RAIL, one list.
 *
 * The bottom bar takes five, so each gets a 44px target on a small phone, and Issues is reached
 * from Promises. Schedule, Enquiries, Tasks, Sources and Campaigns are the others: all five are
 * marked `railOnly`, so the desk shows each as its own destination and the phone bar does not grow. A rail
 * 248px wide has room for words the bar has not got, and that is the only thing the extra width is
 * spent on here.
 *
 * TASKS IS DESK WORK AND ONLY DESK WORK, which is why it is one of the rail's own words. A
 * follow-up is written down while somebody is on the phone at a table, and worked from the same
 * table; nobody reads a list of them standing in a driveway. The office list of CALLS is the second
 * tab of that screen rather than a word of its own, because "who rang" and "what did somebody say
 * she would do about it" are one question asked twice, and the rail is already at ten.
 *
 * SCHEDULE AND TODAY ARE ONE SCREEN ON TWO PATHS. The artboard draws both, and the honest way to
 * answer that is not a second screen but a second route onto the one that already exists: `/today`
 * is the day in front of you and `/schedule` is the same view of the calendar, reached by the word
 * a person looking for next Thursday goes hunting for. The active mark follows the path, so each
 * word lights up on its own and neither lights up on the other.
 *
 * SETTINGS IS THE LAST WORD AND IT SITS UNDER THE RULE AT THE FOOT OF THE RAIL, marked `foot`, so
 * it is out of the daily list without being out of reach. It is the standing words the product
 * runs on rather than a day's work: the scope a job sheet freezes, and the days we are closed.
 * There is no settings word on the phone bar, because neither of those is typed in a driveway.
 */
const OFFICE_NAV: readonly NavItem[] = [
  { to: '/today', label: 'Today' },
  { to: '/schedule', label: 'Schedule', railOnly: true },
  { to: '/enquiries', label: 'Enquiries', railOnly: true },
  // Lit on its three tabs: the follow-ups, the calls under it, and the texts at their own path.
  { to: '/tasks', label: 'Tasks', railOnly: true, also: ['/messages'] },
  { to: '/clients', label: 'Clients' },
  { to: '/money', label: 'Money' },
  // Where households came from, which is a question asked at a desk with the plan open and never
  // between two houses, so it is one of the rail's own words and takes nothing off the phone bar.
  { to: '/sources', label: 'Sources', railOnly: true },
  // What one batch of letters or one ad flight came to: the Sources question asked of a campaign
  // rather than a channel, at the same desk, so it sits beside that word and off the phone bar.
  { to: '/campaigns', label: 'Campaigns', railOnly: true },
  { to: '/breaches', label: 'Promises' },
  { to: '/crew', label: 'Crew' },
  { to: '/settings', label: 'Settings', foot: true },
];

/** A cleaner has one screen, so she gets no navigation at all rather than a bar with one word. */
const CLEANER_NAV: readonly NavItem[] = [];

/**
 * The other systems we run, one press away in the top bar for the office. The phone system's live
 * wall is its own Worker on its own hostname with its own sign-in; ops links to it and holds
 * nothing of it (isolation rule 4: no cross-system binding, and a link is not one).
 */
const OFFICE_LINKS = [
  {
    href: 'https://phone.faithfullykept.com/wall',
    label: 'Call wall',
    hint: 'The phone system, live: who is calling and who is on the line. Opens in a new tab.',
  },
] as const;

/**
 * THE DOORS IN THE TOP BAR, by who is looking (the owner, 2026-10-17). An owner or a manager is
 * handed `systems` by `GET /api/me`, this deployment's `SYSTEM_LINKS`: the wall in it is the Call
 * wall door, so staging's leads to the phone system's staging wall, and the rest go behind the
 * Systems menu. The office is handed no list and keeps the door above exactly as it was; a cleaner
 * gets no door at all, whatever arrives. What is drawn is UX: the server decides who is handed what.
 */
function doorsFor(me: Me): {
  readonly links?: readonly ExternalLink[];
  readonly systems?: readonly ExternalLink[];
} {
  if (me.role === 'cleaner') return {};
  const handed = me.systems ?? [];
  const wall = handed.find((link) => link.key === CALL_WALL_KEY);
  return {
    links: wall ? [wall] : OFFICE_LINKS,
    systems: handed.filter((link) => link.key !== CALL_WALL_KEY),
  };
}

/**
 * The one prefix answered with no sign-in, her link's and Stripe's return alike. Checked before the
 * boot effect asks who this is.
 */
const PUBLIC_PREFIX = '/r/';

export default function App() {
  const place = usePlace();
  const navigate = useNavigate();
  const isPublic = place.pathname.startsWith(PUBLIC_PREFIX);
  const [signedIn, setSignedIn] = useState<Me | null>(null);
  const [refusal, setRefusal] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [unread, setUnread] = useState(0);
  const [noReplyYet, setNoReplyYet] = useState(0);
  const [openTasks, setOpenTasks] = useState(0);
  /** The business day, asked for again after boot. Null until an answer disagrees with the first. */
  const [today, setToday] = useState<string | null>(null);

  /**
   * The person, with whatever day is current. `withToday` returns the same object while the day
   * has not moved, so this memo is a new object exactly once per day and never on a re-render.
   */
  const me = useMemo(
    () => (signedIn === null ? null : withToday(signedIn, today)),
    [signedIn, today],
  );

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  /**
   * A SESSION CAN END WHILE SOMEBODY IS WORKING, and when it does this is the screen she needs.
   *
   * /api/me is asked once, at the start. After that the cookie can stop verifying, or an owner can
   * end the session from the crew screen, and the answer arrives at whichever screen happened to
   * be reading: 401 `not_signed_in`, 403 `session_revoked`, 403 `not_enrolled`. Each screen used to
   * draw that as its own failure, which means a sentence saying "sign in again" beside a button
   * that repeats the request forever, and no way to reach a sign-in link at all without reloading
   * the document by hand.
   *
   * It is not a screen's failure. It is the same refusal everywhere behind it, so `api.ts` says so
   * once and this lifts it into `refusal`, where `SignedOut` explains which of the three happened
   * and offers the link that fixes it. Step-up is not one of them: that session is still good and
   * the screen that asked answers it beside what it was drawing.
   */
  useEffect(() => onSessionEnded(setRefusal), []);

  useEffect(() => {
    if (isPublic) return;
    let live = true;
    setSignedIn(null);
    setToday(null);
    setRefusal(null);
    // A refused sign-in comes back from /api/auth as a redirect to this page with the code and the
    // sentence in the query, so the refusal is a screen and not a document. Read once, then taken
    // off the address, so a refresh or a retry asks /api/me instead of repeating it.
    const params = new URLSearchParams(place.search);
    const refusedCode = params.get('signin');
    if (refusedCode) {
      setRefusal(
        new ApiError(0, refusedCode, params.get('message') ?? 'That sign-in did not go through.'),
      );
      navigate(place.pathname, { replace: true });
      return;
    }
    getMe()
      .then((answer) => {
        if (live) setSignedIn(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setRefusal(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'This application could not work out who you are.'),
        );
      });
    return () => {
      live = false;
    };
  }, [attempt, isPublic]);

  const countUnread = useCallback(() => {
    listNotifications()
      .then((answer) => setUnread(answer.unread))
      .catch(() => {
        // The bell shows the last count it had. The list screen says what went wrong, in words.
      });
  }, []);

  /**
   * The digit beside Enquiries in the rail: how many people asked and have had no answer. Its own
   * endpoint answers it with two counts and no rows, so it rides the bell's schedule rather than
   * earning one of its own. A cleaner never asks: the endpoint is office only and she has no rail.
   */
  const countWaiting = useCallback(() => {
    countEnquiries()
      .then((answer) => setNoReplyYet(answer.noReplyYet))
      .catch(() => {
        // The rail keeps the number it had. Nothing is decided from it; the screen behind the
        // word is where the enquiries themselves are, and it says what went wrong in words.
      });
  }, []);

  /**
   * The digit beside Tasks: how many follow-ups are still open. Its own endpoint answers it with
   * two counts from one statement of conditional sums, the same shape the enquiries badge uses and
   * for the same reason: the list behind the word is every column of every open task, and a number
   * asked again on every route change is not worth that. Office only, like the screen it counts.
   */
  const countFollowUps = useCallback(() => {
    countTasks()
      .then((answer) => setOpenTasks(answer.open))
      .catch(() => {
        // The rail keeps the number it had. Nothing is decided from it, and the screen behind the
        // word says what went wrong in words.
      });
  }, []);

  /**
   * WHAT DAY IS IT NOW. The same read the application boots on, answered by the server rather than
   * by this browser's clock, because the business day is the server's to say. `setToday` with the
   * string it already holds is not a state change, so an unchanged day re-renders nothing.
   */
  const refreshDay = useCallback(() => {
    getMe()
      .then((answer) => setToday(answer.today))
      .catch(() => {
        // The day in hand stands. A refusal that means this browser is nobody has already been
        // said out loud by api.ts, and the sign-in screen is what answers that.
      });
  }, []);

  /** The path the day was last answered for. The boot read answers for the one it landed on. */
  const dayAskedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!signedIn) return;
    if (dayAskedFor.current !== place.pathname) {
      // Never on the first pass. The read that just told us who this is told us the day with it,
      // and two identical reads at every boot is a request nobody is waiting for.
      if (dayAskedFor.current !== null) refreshDay();
      dayAskedFor.current = place.pathname;
    }
    countUnread();
    if (signedIn.role !== 'cleaner') {
      countWaiting();
      countFollowUps();
    }
  }, [signedIn, place.pathname, refreshDay, countUnread, countWaiting, countFollowUps]);

  /**
   * The tab coming back to the front is somebody looking at it again, which is exactly the moment
   * an overnight tab is about to be believed. One cheap read, no timer, and nothing at all while
   * the phone is in a pocket.
   */
  useEffect(() => {
    if (!signedIn) return;
    const look = () => {
      if (document.visibilityState === 'visible') refreshDay();
    };
    document.addEventListener('visibilitychange', look);
    return () => document.removeEventListener('visibilitychange', look);
  }, [signedIn, refreshDay]);

  if (isPublic) {
    return (
      <Routes>
        {/* A written path outranks a parameter, so the return is never read as a token, and a
            token is sixty-four hex characters, which the return's word is not. */}
        <Route path={PAYMENT_RETURN_PATH} element={<PaymentReturn />} />
        <Route path="/r/:token" element={<PublicPage />} />
      </Routes>
    );
  }

  if (refusal) {
    // The sign-in comes back to this page, query and all: the call wall opens `/find?number=...`
    // in a new tab, and a lapsed session there must not lose the number (ops #84).
    return (
      <SignedOut error={refusal} onRetry={retry} returnPath={`${place.pathname}${place.search}`} />
    );
  }

  if (!me) {
    return (
      <div className="min-h-dvh bg-cream">
        <Busy what="Faithfully Kept" />
      </div>
    );
  }

  const isCleaner = me.role === 'cleaner';
  const doors = doorsFor(me);

  return (
    <AppShell
      me={me}
      nav={isCleaner ? CLEANER_NAV : OFFICE_NAV}
      links={doors.links}
      systems={doors.systems}
      // Who is on the line now, polled while the tab is in front (ops #84): the office's, on every
      // office screen, and never mounted for a cleaner, whom the server refuses the read besides.
      banner={isCleaner ? null : <RingingNow />}
      unread={unread}
      // A zero is not news, so nothing is drawn until somebody is actually waiting.
      counts={{
        '/enquiries': noReplyYet > 0 ? noReplyYet : undefined,
        '/tasks': openTasks > 0 ? openTasks : undefined,
      }}
    >
      {isCleaner ? (
        <Routes>
          <Route path="/my-work" element={<MyWork me={me} />} />
          <Route path="/visits/:id" element={<VisitDetail me={me} />} />
          <Route path="/visits/:id/sheet" element={<JobSheet me={me} />} />
          <Route path="/notifications" element={<Notifications onChanged={countUnread} />} />
          <Route path="*" element={<Navigate to="/my-work" replace />} />
        </Routes>
      ) : (
        <Routes>
          {/* KEYED BY THE DAY. The screen keeps which day it is showing in its own state, taken
              from `me.today` when it mounts, so a day that moves under an open tab would leave it
              on yesterday with the right words above it. A new key is a new screen on the new day,
              and the key changes exactly once, when the day does. */}
          <Route path="/today" element={<Today key={me.today} me={me} />} />
          {/* The schedule IS Today: one component, two paths, and the rail marks whichever one
              you came in by. A second screen drawing the same two days would be a second screen
              to keep in step with this one.

              THE HEADING COMES FROM THE ROUTE, and that is the whole of the difference. Somebody
              who pressed Schedule and read "Today" at the top of the page had reason to think the
              press had not worked; the words belong to the door you came in by, and the screen
              behind them is the same screen either way. */}
          <Route path="/schedule" element={<Today key={me.today} me={me} heading="Schedule" />} />
          <Route path="/visits/:id" element={<VisitDetail me={me} />} />
          <Route path="/visits/:id/sheet" element={<JobSheet me={me} />} />
          <Route path="/agreements/:id" element={<AgreementDetail today={me.today} />} />
          <Route path="/closed-days" element={<ClosedDays />} />
          <Route path="/clients" element={<ClientList />} />
          <Route path="/clients/new" element={<NewClient />} />
          <Route path="/clients/:id" element={<ClientDetail me={me} />} />
          {/* A page per home (ops #84): office only on the server, and no route in a cleaner's branch. */}
          <Route path="/homes/:id" element={<HomeDetail me={me} />} />
          <Route path="/enquiries" element={<Enquiries me={me} />} />
          {/* The find door the two walls open (ops #84): a number or a lead id to the household or
              the enquiry. Office only on the server; a cleaner's branch has no route to it, and lands
              on their own work as every unknown path does. */}
          <Route path="/find" element={<Find />} />
          {/* The follow-ups, and the office lists of calls and of texts as their second and
              third tabs rather than words of their own in the rail. `/tasks` is the active rail
              word for all three. Texts is read only until ops #26 gives it its actions. */}
          <Route path="/tasks" element={<Tasks me={me} />} />
          <Route path="/tasks/calls" element={<Calls me={me} />} />
          {/* One call, as the phone system tells it (ops #13): the owners' and the managers'
              on the server, where the office role and a cleaner are refused. The office branch only,
              and a cleaner has no route to it at all, which is UX and not the control. */}
          <Route path="/calls/:callSid" element={<CallDetail />} />
          <Route path="/messages" element={<Messages me={me} />} />
          <Route path="/crew" element={<Crew me={me} />} />
          <Route path="/money" element={<Money />} />
          {/* Reports A and B. Office and owner only on the server too, where it counts: a cleaner
              has her own routes below and never reaches this branch at all. */}
          <Route path="/sources" element={<Sources me={me} />} />
          {/* The campaigns register: what a batch of letters or an ad flight cost, and who came
              from its word. A word in the rail beside Sources, and office only on the server. */}
          <Route path="/campaigns" element={<Campaigns me={me} />} />
          {/* What the business spent, which is where the cost column comes from. It is not a word
              in the rail: it is one press from the report it feeds, which is where somebody
              looking at a dash in that column is standing. */}
          <Route path="/spend" element={<Spend me={me} />} />
          <Route path="/invoices/:id" element={<InvoiceDetail me={me} />} />
          <Route path="/issues" element={<Issues />} />
          <Route path="/breaches" element={<Promises />} />
          <Route path="/notifications" element={<Notifications onChanged={countUnread} />} />
          {/* The standing words the product runs on: the scope a job sheet freezes, and the days
              we are closed. Office only here and office only on the server, where it counts. */}
          <Route path="/settings" element={<Settings me={me} />} />
          <Route path="*" element={<Navigate to="/today" replace />} />
        </Routes>
      )}
    </AppShell>
  );
}
